import { useCallback } from 'react';

import { useSafeNavigation, useSafeRouter } from '@sg/garnish';

import { useIsExpoRouterEnabled } from '@order/shared/hooks';

import { type PartialProduct } from '../../../../graphql';
import { type BagScreenProps } from '../../../../navigation/AppNavigation.props';

/**
 * Hook that allows navigating to the correct edit customization page.
 */
export const useNavigateToCustomizationEditScreen = (props: Params) => {
  const { addressId = '', restaurantSlug = '', isDelivery = false } = props;

  const isExpoRouterEnabled = useIsExpoRouterEnabled();
  const navigation = useSafeNavigation<BagScreenProps['navigation']>();
  const router = useSafeRouter();

  const navigateToCustomizationEditScreen = useCallback(
    (params: NavigateToCustomizationEditParams) => {
      const { lineItemId, product } = params;
      const productSlug = product?.slug ?? '';

      if (isDelivery) {
        if (isExpoRouterEnabled) {
          router?.navigate({
            pathname: '/delivery/[addressId]/[productSlug]/edit/[lineItemId]',
            params: { addressId, productSlug, lineItemId },
          });
          return;
        }

        navigation?.navigate('CustomizationDeliveryEdit', {
          addressId,
          productSlug,
          lineItemId,
        });

        return;
      }

      if (isExpoRouterEnabled) {
        router?.navigate({
          pathname: '/[restaurantSlug]/[productSlug]/edit/[lineItemId]',
          params: { restaurantSlug, productSlug, lineItemId },
        });
        return;
      }

      navigation?.navigate('CustomizationEdit', {
        restaurantSlug,
        productSlug,
        lineItemId,
      });
    },
    [addressId, isDelivery, isExpoRouterEnabled, navigation, restaurantSlug, router],
  );

  return { navigateToCustomizationEditScreen };
};

// ─── Types ──────────────────────────────────────────────────────────────────

type Params = Readonly<{
  isDelivery?: boolean;
  addressId?: string;
  restaurantSlug?: string;
}>;

type NavigateToCustomizationEditParams = Readonly<{
  lineItemId: string;
  product: PartialProduct;
}>;
